import * as pdfMake from "pdfmake/build/pdfmake.js";
import DateUtil from "@/plugins/date-util/DateUtil";
import { EmergencyTransportPatientModel } from "@/commons/services/EmergencyTransportPatients";

/**
 * ### 救急搬送情報詳細のPDF出力操作クラス
 */
export class EmergencyTransportPatientPdf {
  private dateUtil: DateUtil;
  private normalFont: string;

  /**
   * ### コンストラクター
   *
   * @param normalFont PDFMake用のBase64フォント文字列
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "EmergencyTransportPatientPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      EmergencyTransportPatientPdf: {
        normal: "EmergencyTransportPatientPdf.ttf",
        bold: "EmergencyTransportPatientPdf.ttf"
      }
    };
  }

  /**
   * ### 救急搬送情報PDFを作成してダウンロードする
   *
   * @param patient 対象の救急搬送患者情報
   * @param fileName 出力ファイル名
   */
  public createAndDownloadPdf(patient: EmergencyTransportPatientModel, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [20, 20, 20, 20],
      info: this.createInfo(),
      content: this.createContent(patient)
    };

    pdfMake.createPdf(docDefinition).download(fileName);
    return;
  }

  /**
   * ### スタイル定義を作成する
   */
  private createStyles(): any {
    return {
      pdfTitle: {
        fontSize: 15,
        bold: true,
        alignment: "center",
        fillColor: "#f2f2f2",
        margin: [0, 3, 0, 3]
      },
      labelCol: {
        fontSize: 9.5,
        fillColor: "#f2f2f2",
        margin: [0, 2, 0, 2]
      },
      valCol: {
        fontSize: 9.5,
        margin: [0, 2, 0, 2]
      }
    };
  }

  /**
   * ### デフォルトスタイルを作成する
   */
  private createDefaultStyle(): any {
    return {
      font: "EmergencyTransportPatientPdf"
    };
  }

  /**
   * ### 文書メタ情報を作成する
   */
  private createInfo(): any {
    return {
      title: "救急搬送シート",
      author: "KONICA MINOLTA"
    };
  }

  /**
   * ### コンテンツを作成する
   */
  private createContent(patient: EmergencyTransportPatientModel): Array<any> {
    const tableBody: Array<any> = [
      // Row 1: タイトル
      [
        {
          text: "救急搬送シート",
          style: "pdfTitle",
          bold: true,
          fillColor: "#f2f2f2",
          colSpan: 4
        },
        {},
        {},
        {}
      ],
      // Row 2: 施設名 / 記入者
      [{ text: "施設名\n記入者", style: "labelCol", fillColor: "#f2f2f2" }, { text: this.formatFacility(patient), style: "valCol", colSpan: 3 }, {}, {}],
      // Row 3: 搬送先病院
      [{ text: "搬送先病院", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.targetFacilityName || "", style: "valCol", colSpan: 3 }, {}, {}],
      // Row 4: 患者氏名 / 生年月日
      [
        { text: "患者氏名", style: "labelCol", fillColor: "#f2f2f2" },
        { text: patient.name || "", style: "valCol" },
        { text: "生年月日", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatBirthday(patient.birthday), style: "valCol" }
      ],
      // Row 5: 性別 / 身長
      [
        { text: "性別", style: "labelCol", fillColor: "#f2f2f2" },
        { text: patient.gender || "", style: "valCol" },
        { text: "身長", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatHeight(patient.height), style: "valCol" }
      ],
      // Row 6: 体重 / 血液型
      [
        { text: "体重", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatWeight(patient.weight), style: "valCol" },
        { text: "血液型", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatBloodType(patient.bloodType), style: "valCol" }
      ],
      // Row 7: 患者家族連絡先 / かかりつけ医
      [
        { text: "患者家族\n連絡先", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatFamilies(patient), style: "valCol" },
        { text: "かかりつけ医", style: "labelCol", fillColor: "#f2f2f2" },
        { text: this.formatPrimaryHospital(patient), style: "valCol" }
      ],
      // Row 8: 救急搬送の理由・経緯
      [{ text: "救急搬送\nの理由・経緯", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.note || "", style: "valCol", colSpan: 3 }, {}, {}],
      // Row 9: 最終バイタルサイン
      [{ text: "最終\nバイタルサイン", style: "labelCol", fillColor: "#f2f2f2" }, { text: this.formatVitals(patient), style: "valCol", colSpan: 3 }, {}, {}],
      // Row 10: 禁忌・アレルギー
      [{ text: "禁忌・アレルギー", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.allergiesAndContraindications || "", style: "valCol", colSpan: 3 }, {}, {}],
      // Row 11: 既往歴
      [{ text: "既往歴", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.medicalHistory || "", style: "valCol", colSpan: 3 }, {}, {}],
      // Row 12: 服薬中の薬
      [{ text: "服薬中の薬", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.medications || "", style: "valCol", colSpan: 3 }, {}, {}],
      // Row 13: その他の連絡事項
      [{ text: "その他の連絡事項", style: "labelCol", fillColor: "#f2f2f2" }, { text: patient.otherInfo || "", style: "valCol", colSpan: 3 }, {}, {}]
    ];

    return [
      {
        table: {
          widths: [110, "*", 110, "*"],
          body: tableBody
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
          paddingLeft: () => 6,
          paddingRight: () => 6,
          paddingTop: () => 4,
          paddingBottom: () => 4
        }
      }
    ];
  }

  /**
   * ### 施設・記入者文字列フォーマット
   */
  private formatFacility(patient: EmergencyTransportPatientModel): string {
    const parts: string[] = [];
    let facilityLine = patient.facilityName || "";
    const infoParts: string[] = [];
    if (patient.facilityAddress) infoParts.push(patient.facilityAddress);
    if (patient.facilityTel) infoParts.push(patient.facilityTel);
    if (infoParts.length > 0) {
      facilityLine += `（${infoParts.join("、")}）`;
    }
    if (facilityLine) parts.push(facilityLine);
    if (patient.assignedStaffName) parts.push(patient.assignedStaffName);
    return parts.join("\n");
  }

  /**
   * ### 身長フォーマット
   */
  private formatHeight(height?: string): string {
    if (!height) return "";
    return height.endsWith("cm") ? height : `${height}cm`;
  }

  /**
   * ### 体重フォーマット
   */
  private formatWeight(weight?: string): string {
    if (!weight) return "";
    return weight.endsWith("kg") ? weight : `${weight}kg`;
  }

  /**
   * ### 血液型フォーマット
   */
  private formatBloodType(bloodType?: string): string {
    if (!bloodType) return "";
    return bloodType.endsWith("型") ? bloodType : `${bloodType}型`;
  }

  /**
   * ### 患者家族連絡先の文字列フォーマット
   */
  private formatFamilies(patient: EmergencyTransportPatientModel): string {
    if (!patient.families || patient.families.length === 0) return "";
    return patient.families
      .map((family) => {
        const tel = family.tel ? ` (${family.tel})` : "";
        return `${family.name}${tel}`;
      })
      .join("\n");
  }

  /**
   * ### かかりつけ医の文字列フォーマット
   */
  private formatPrimaryHospital(patient: EmergencyTransportPatientModel): string {
    const parts: string[] = [];
    if (patient.primaryHospitalName) parts.push(patient.primaryHospitalName);
    if (patient.primaryDoctorName) parts.push(patient.primaryDoctorName);
    if (patient.primaryHospitalTel) parts.push(patient.primaryHospitalTel);
    return parts.join("\n");
  }

  /**
   * ### バイタルサイン文字列フォーマット
   */
  private formatVitals(patient: EmergencyTransportPatientModel): string {
    const lines: string[] = [];

    const bp = this.formatBloodPressure(patient.maxBloodPressure, patient.minBloodPressure);
    if (bp) {
      lines.push(`血圧：${bp}`);
    }

    if (patient.pulse != null) {
      lines.push(`脈拍：${patient.pulse}回/分`);
    }

    if (patient.respiration != null) {
      lines.push(`呼吸：${patient.respiration}回/分`);
    }

    if (patient.bodyTemperature) {
      const temp = patient.bodyTemperature.endsWith("℃") ? patient.bodyTemperature : `${patient.bodyTemperature}℃`;
      lines.push(`体温：${temp}`);
    }

    if (patient.spo2 != null) {
      lines.push(`SpO2：${patient.spo2}%`);
    }

    if (patient.specialNotes) {
      lines.push("【特記事項】");
      lines.push(patient.specialNotes);
    }

    return lines.join("\n");
  }

  /**
   * ### 生年月日フォーマット
   */
  private formatBirthday(birthday?: number): string {
    if (!birthday) return "";
    return this.dateUtil.convertNumberToString(birthday, "yyyy年mm月dd日", false);
  }

  /**
   * ### 血圧フォーマット
   */
  private formatBloodPressure(max?: number, min?: number): string {
    if (max != null && min != null) {
      return `最高 ${max}mmHg、最低 ${min}mmHg`;
    } else if (max != null) {
      return `最高 ${max}mmHg`;
    } else if (min != null) {
      return `最低 ${min}mmHg`;
    }
    return "";
  }
}
